import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Link, useLocation } from "react-router-dom";
import { Phone, Menu } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";

export function Navbar() {
  const { language, setLanguage, t } = useLanguage();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 flex flex-col transition-all duration-500 ${scrolled ? "translate-y-0" : ""}`}>
      {/* Top Bar */}
      <div className={`bg-navy-950 border-b border-white/10 hidden lg:block transition-all duration-500 overflow-hidden ${scrolled ? "h-0 opacity-0 border-none" : "h-10 opacity-100"}`}>
        <div className="container mx-auto px-4 h-10 flex items-center justify-end gap-6">
          <a href="tel:7739809090" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors text-sm font-medium">
            <Phone className="w-3.5 h-3.5" />
            773-980-9090
          </a>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 text-white/80 hover:bg-white/10 hover:text-white flex items-center justify-center gap-1.5 px-2 w-[70px] shrink-0">
                <span className="text-base leading-none">{language === 'en' ? '🇺🇸' : '🇷🇺'}</span>
                <span className="text-xs font-semibold">{language === 'en' ? 'ENG' : 'RUS'}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-navy-950 border border-white/10 text-white min-w-[100px]">
              <DropdownMenuItem onClick={() => setLanguage('en')} className={`cursor-pointer hover:bg-white/10 focus:bg-white/10 focus:text-white flex items-center gap-2 text-xs ${language === 'en' ? 'text-primary' : ''}`}>
                <span className="text-base leading-none">🇺🇸</span> ENG
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setLanguage('ru')} className={`cursor-pointer hover:bg-white/10 focus:bg-white/10 focus:text-white flex items-center gap-2 text-xs ${language === 'ru' ? 'text-primary' : ''}`}>
                <span className="text-base leading-none">🇷🇺</span> RUS
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Main Bar */}
      <div className={`transition-all duration-500 ${scrolled ? "bg-navy-950/80 backdrop-blur-xl py-1 shadow-2xl" : "bg-navy-950/90 backdrop-blur-md py-0"} border-b border-white/10`}>
        <div className={`container mx-auto px-4 flex items-center justify-between transition-all duration-500 ${scrolled ? "h-14 md:h-16" : "h-16 md:h-20"}`}>
          <Link to="/" className="flex items-center gap-2 shrink-0 mr-4 xl:mr-8">
            <img 
              src="https://vibe.filesafe.space/1777392978847194987/attachments/7e47838b-fe62-45a6-9466-14b47e07dd0c.webp" 
              srcSet="https://vibe.filesafe.space/1777392978847194987/attachments/7e47838b-fe62-45a6-9466-14b47e07dd0c.webp?w=150 150w, https://vibe.filesafe.space/1777392978847194987/attachments/7e47838b-fe62-45a6-9466-14b47e07dd0c.webp?w=300 300w"
              sizes="150px"
              crossOrigin="anonymous" 
              alt="AITIM Group Logo" 
              width={150} 
              height={48} 
              fetchPriority="high" 
              className="h-12 md:h-14 xl:h-16 w-auto object-contain shrink-0" 
            />
          </Link>
          
          <nav className="hidden lg:flex items-center gap-3 xl:gap-6 text-sm font-medium text-white/80 whitespace-nowrap">
            <Link to="/" className={`hover:text-white transition-colors ${location.pathname === '/' ? 'text-white border-b-2 border-primary pb-1' : ''}`}>{t('nav.home')}</Link>
            <Link to="/trucking" className={`hover:text-white transition-colors ${location.pathname === '/trucking' ? 'text-white border-b-2 border-primary pb-1' : ''}`}>{t('nav.services')}</Link>
            <Link to="/consulting" className={`hover:text-white transition-colors ${location.pathname === '/consulting' ? 'text-white border-b-2 border-primary pb-1' : ''}`}>{t('nav.consulting')}</Link>
            <Link to="/business-services" className={`hover:text-white transition-colors ${location.pathname === '/business-services' ? 'text-white border-b-2 border-primary pb-1' : ''}`}>{language === 'ru' ? 'Бизнес-услуги' : 'Business'}</Link>
            <Link to="/media" className={`hover:text-white transition-colors ${location.pathname === '/media' ? 'text-white border-b-2 border-primary pb-1' : ''}`}>{t('nav.media')}</Link>
            
            <Link to="/english" className={`group relative inline-flex items-center justify-center rounded-full p-[1.5px] text-xs xl:text-sm font-bold text-white transition-all duration-300 hover:scale-105 hover:shadow-[0_0_20px_rgba(37,99,235,0.4)] ${location.pathname === '/english' ? 'ring-2 ring-white ring-offset-2 ring-offset-navy-950' : ''}`}>
              <span className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-600 via-primary to-cyan-400 bg-[length:200%_auto] animate-pulse"></span>
              <span className="relative flex items-center gap-1.5 rounded-full bg-navy-950 px-3 py-1 xl:py-1.5 transition-all duration-300 group-hover:bg-transparent">
                <span className="drop-shadow-md">
                  {language === 'ru' ? 'Английский' : 'Trucker\'s English'}
                </span>
                <span className="flex items-center gap-1 bg-white/20 px-1.5 py-0.5 rounded-full border border-white/30 text-[9px] uppercase tracking-wider shadow-sm">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-500"></span>
                  </span>
                  NEW
                </span>
              </span>
            </Link>

            <Link to="/driverslibrary" className={`hover:text-white transition-colors ${location.pathname === '/driverslibrary' ? 'text-white border-b-2 border-primary pb-1' : ''}`}>{language === 'ru' ? 'Библиотека водителя' : 'Driver\'s Library'}</Link>
            
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 hover:text-white transition-colors outline-none cursor-pointer">
                {language === 'ru' ? 'Ещё' : 'More'}
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-navy-950 border border-white/10 text-white min-w-[160px] p-1">
                <DropdownMenuItem asChild className="cursor-pointer hover:bg-white/10 focus:bg-white/10 focus:text-white py-2">
                  <a href="https://community.aitimgroup.com" target="_blank" rel="noopener noreferrer">
                    {language === 'ru' ? 'Участники' : 'Members'}
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="cursor-pointer hover:bg-white/10 focus:bg-white/10 focus:text-white py-2">
                  <Link to="/awards">{language === 'ru' ? 'Награды' : 'Awards'}</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="cursor-pointer hover:bg-white/10 focus:bg-white/10 focus:text-white py-2">
                  <Link to="/about">{language === 'ru' ? 'О нас' : 'About Us'}</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="cursor-pointer hover:bg-white/10 focus:bg-white/10 focus:text-white py-2">
                  <Link to="/resources">{language === 'ru' ? 'Ресурсы' : 'Resources'}</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="cursor-pointer hover:bg-white/10 focus:bg-white/10 focus:text-white py-2">
                  <Link to="/job-application">{language === 'ru' ? 'Заявка на работу' : 'Job Application'}</Link>
                </DropdownMenuItem>

              </DropdownMenuContent>
            </DropdownMenu>
            <Link to="/free-consultation">
              <Button className="bg-primary hover:bg-primary/90 text-white rounded-sm px-4 xl:px-6 h-9 xl:h-10 ml-1 xl:ml-2">
                {t('nav.book')}
              </Button>
            </Link>
          </nav>

          {/* Mobile Menu */}
          <div className="flex items-center gap-2 lg:hidden">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 text-white/80 hover:bg-white/10 hover:text-white flex items-center justify-center gap-1.5 px-2 shrink-0">
                  <span className="text-base leading-none">{language === 'en' ? '🇺🇸' : '🇷🇺'}</span>
                  <span className="text-xs font-semibold">{language === 'en' ? 'ENG' : 'RUS'}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-navy-950 border border-white/10 text-white min-w-[100px]">
                <DropdownMenuItem onClick={() => setLanguage('en')} className={`cursor-pointer hover:bg-white/10 focus:bg-white/10 focus:text-white flex items-center gap-2 text-xs ${language === 'en' ? 'text-primary' : ''}`}>
                  <span className="text-base leading-none">🇺🇸</span> ENG
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLanguage('ru')} className={`cursor-pointer hover:bg-white/10 focus:bg-white/10 focus:text-white flex items-center gap-2 text-xs ${language === 'ru' ? 'text-primary' : ''}`}>
                  <span className="text-base leading-none">🇷🇺</span> RUS
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-white" aria-label="Open menu">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-navy-950 border-white/10 text-white w-[300px] sm:w-[400px] overflow-y-auto">
                <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
                <div className="flex flex-col gap-6 mt-8">
                  <Link to="/" className={`text-lg font-medium transition-colors ${location.pathname === '/' ? 'text-primary' : 'hover:text-primary'}`}>{t('nav.home')}</Link>
                  <Link to="/trucking" className={`text-lg font-medium transition-colors ${location.pathname === '/trucking' ? 'text-primary' : 'hover:text-primary'}`}>{t('nav.services')}</Link>
                  <Link to="/consulting" className={`text-lg font-medium transition-colors ${location.pathname === '/consulting' ? 'text-primary' : 'hover:text-primary'}`}>{t('nav.consulting')}</Link>
                  <Link to="/business-services" className={`text-lg font-medium transition-colors ${location.pathname === '/business-services' ? 'text-primary' : 'hover:text-primary'}`}>{language === 'ru' ? 'Бизнес-услуги' : 'Business Services'}</Link>
                  <Link to="/media" className={`text-lg font-medium transition-colors ${location.pathname === '/media' ? 'text-primary' : 'hover:text-primary'}`}>{t('nav.media')}</Link>
                  <Link to="/english" className="text-lg font-medium text-primary transition-colors flex items-center gap-2">
                    {language === 'ru' ? 'Английский' : 'Trucker\'s English'}
                    <span className="bg-red-500 text-white text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider font-bold">New</span>
                  </Link>
                  <Link to="/driverslibrary" className={`text-lg font-medium transition-colors ${location.pathname === '/driverslibrary' ? 'text-primary' : 'hover:text-primary'}`}>{language === 'ru' ? 'Библиотека водителя' : 'Driver\'s Library'}</Link>
                  
                  <div className="h-px bg-white/10 w-full my-2"></div>
                  
                  <a href="https://community.aitimgroup.com" target="_blank" rel="noopener noreferrer" className="text-lg font-medium hover:text-primary transition-colors">
                    {language === 'ru' ? 'Участники' : 'Members'}
                  </a>
                  <Link to="/awards" className={`text-lg font-medium transition-colors ${location.pathname === '/awards' ? 'text-primary' : 'hover:text-primary'}`}>{language === 'ru' ? 'Награды' : 'Awards'}</Link>
                  <Link to="/about" className={`text-lg font-medium transition-colors ${location.pathname === '/about' ? 'text-primary' : 'hover:text-primary'}`}>{language === 'ru' ? 'О нас' : 'About Us'}</Link>
                  <Link to="/resources" className={`text-lg font-medium transition-colors ${location.pathname === '/resources' ? 'text-primary' : 'hover:text-primary'}`}>{language === 'ru' ? 'Ресурсы' : 'Resources'}</Link>
                  <Link to="/job-application" className={`text-lg font-medium transition-colors ${location.pathname === '/job-application' ? 'text-primary' : 'hover:text-primary'}`}>{language === 'ru' ? 'Заявка на работу' : 'Job Application'}</Link>
                  
                  <Link to="/free-consultation">
                    <Button className="bg-primary hover:bg-primary/90 text-white mt-4 w-full">
                      {t('nav.book')}
                    </Button>
                  </Link>
                  
                  <a href="tel:7739809090" className="flex items-center justify-center gap-2 text-white/80 hover:text-white transition-colors mt-4">
                    <Phone className="w-4 h-4" />
                    773-980-9090
                  </a>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
